import React, { useState, useEffect } from 'react';
import {
    CalendarDaysIcon,
    MapPinIcon,
    UserGroupIcon,
    CheckCircleIcon,
    ArrowPathIcon,
    ShieldCheckIcon,
    ShoppingBagIcon
} from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../../config/apiConfig';
import { Booking } from '../../types';
import { currencyService } from '../../services/currencyService';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/apiService';

/**
 * MyBookings Dashboard
 * Allows guests to track their expeditions using browser-stored IDs or account email.
 */
const MyBookings: React.FC = () => {
    const { user, isAuthenticated } = useAuth();
    const [bookings, setBookings] = useState<Booking[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchAllBookings = async () => {
            setIsLoading(true);
            try {
                const fetchedBookingsMap = new Map<string, Booking>();

                // 1. Fetch by email if authenticated
                if (isAuthenticated && user?.email) {
                    try {
                        const emailBookings = await apiService.request<Booking[]>(`/bookings/email/${user.email}`);
                        if (emailBookings && Array.isArray(emailBookings)) {
                            emailBookings.forEach(b => fetchedBookingsMap.set(b.id, b));
                        }
                    } catch (err) {
                        console.error('Failed to fetch bookings by email:', err);
                    }
                }

                // 2. Fetch by stored IDs in localStorage
                const storedIds = JSON.parse(localStorage.getItem('st_my_bookings') || '[]');
                if (storedIds.length > 0) {
                    for (const id of storedIds) {
                        if (fetchedBookingsMap.has(id)) continue; // Skip if already fetched by email

                        try {
                            const booking = await apiService.getBookingById(id);
                            if (booking) {
                                fetchedBookingsMap.set(booking.id, booking);
                            }
                        } catch (err) {
                            console.error(`Failed to fetch booking ${id}:`, err);
                        }
                    }
                }

                // Convert map back to array and sort by date (newest first)
                const finalBookings = Array.from(fetchedBookingsMap.values())
                    .sort((a, b) => new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime());

                setBookings(finalBookings);
            } catch (err) {
                console.error('Error loading expeditions:', err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAllBookings();
    }, [user?.email, isAuthenticated]);

    const getStatusStyle = (status: string) => {
        if (!status) return 'bg-gray-100 text-gray-700 border-gray-200';
        switch (status.toLowerCase()) {
            case 'confirmed':
            case 'paid':
            case 'verified':
                return 'bg-green-100 text-green-700 border-green-200';
            case 'pending':
                return 'bg-blue-100 text-blue-700 border-blue-200';
            case 'rejected':
                return 'bg-red-100 text-red-700 border-red-200';
            default:
                return 'bg-gray-100 text-gray-700 border-gray-200';
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-white flex flex-col items-center justify-center p-10">
                <ArrowPathIcon className="w-12 h-12 text-blue-600 animate-spin mb-4" />
                <p className="text-gray-500 font-bold uppercase tracking-widest text-xs">Syncing Expedition Data...</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-neutral-50 p-6 md:p-12 lg:p-20">
            <div className="max-w-6xl mx-auto">
                <header id="my-bookings-header" className="mb-16 flex flex-col md:flex-row md:items-end justify-between gap-6">
                    <div>
                        <h1 className="text-5xl font-black text-slate-900 tracking-tighter mb-4 uppercase">My Expeditions</h1>
                        <p className="text-slate-500 font-medium max-w-xl">
                            Track your scheduled safaris and payment progress. Your sessions are saved locally for privacy.
                        </p>
                    </div>
                    <div className="flex items-center gap-3 bg-white p-4 rounded-3xl border border-blue-100 shadow-sm">
                        <ShieldCheckIcon className="w-8 h-8 text-blue-600" />
                        <div>
                            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none">Status</p>
                            <p className="text-sm font-bold text-slate-900">Encrypted Session</p>
                        </div>
                    </div>
                </header>

                {bookings.length === 0 ? (
                    <div className="bg-white rounded-[3rem] p-20 text-center border border-dashed border-gray-200">
                        <div className="w-20 h-20 bg-blue-50 text-blue-600 rounded-3xl flex items-center justify-center mx-auto mb-8">
                            <ShoppingBagIcon className="w-10 h-10" />
                        </div>
                        <h2 className="text-2xl font-black text-slate-900 mb-4 uppercase">No Scheduled Expeditions</h2>
                        <p className="text-slate-500 mb-10 max-w-md mx-auto">You haven't booked any safaris yet. Start your journey today!</p>
                        <a href="/tours" className="inline-flex px-10 py-4 bg-blue-600 text-white font-black rounded-2xl hover:bg-blue-700 transition-all uppercase text-sm tracking-widest active:scale-95">
                            Explore Tours
                        </a>
                    </div>
                ) : (
                    <div className="grid gap-8 lg:grid-cols-2">
                        {bookings.map((booking) => (
                            <div key={booking.id} className="bg-white rounded-[2.5rem] border border-slate-100 shadow-xl shadow-slate-200/40 overflow-hidden hover:border-blue-200 transition-all group">
                                <div className="p-5 md:p-6">
                                    <div className="flex justify-between items-start mb-6">
                                        <div className="p-2 bg-blue-50 text-blue-600 rounded-xl group-hover:bg-blue-600 group-hover:text-white transition-colors duration-500">
                                            <MapPinIcon className="w-5 h-5" />
                                        </div>
                                        <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${getStatusStyle(booking.status)}`}>
                                            {booking.status}
                                        </span>
                                    </div>

                                    <h3 className="text-lg font-black text-slate-900 uppercase tracking-tighter mb-1.5 leading-tight">{booking.tourName}</h3>
                                    <div className="flex items-center gap-3 text-slate-400 font-bold text-[10px] uppercase tracking-widest mb-6">
                                        <span className="flex items-center gap-1.5"><CalendarDaysIcon className="w-3.5 h-3.5 text-blue-600" /> {new Date(booking.startDate || '').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                                        <span className="flex items-center gap-1.5"><UserGroupIcon className="w-3.5 h-3.5 text-blue-600" /> {typeof booking.participants === 'number' ? booking.participants : (booking.participants?.length || 0)} PAX</span>
                                    </div>

                                    <div className="grid grid-cols-2 gap-3 pt-5 border-t border-slate-50">
                                        <div className="p-4 bg-slate-50 rounded-2xl">
                                            <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-0.5">Total Payload</p>
                                            <p className="text-sm font-black text-slate-900">{booking.currency} {booking.totalAmount?.toLocaleString()}</p>
                                        </div>
                                        <div className="p-4 bg-emerald-50 rounded-2xl">
                                            <p className="text-[8px] font-black text-emerald-600/60 uppercase tracking-widest mb-0.5">Verified Credits</p>
                                            <p className="text-sm font-black text-emerald-600">{booking.currency} {(booking.totalPaid || 0).toLocaleString()}</p>
                                        </div>
                                    </div>

                                    {booking.status === 'pending' && (
                                        <div className="mt-5 p-4 bg-blue-50/50 rounded-2xl border border-blue-100/50 flex flex-col md:flex-row md:items-center justify-between gap-3">
                                            <p className="text-[10px] font-bold text-blue-700">Financial Auth Pending</p>
                                            <button className="text-[9px] font-black text-blue-600 uppercase tracking-widest hover:underline flex-shrink-0">Complete Payment</button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MyBookings;
